/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, transformAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '../app.actions';
import * as fromAppRoot from '../app.reducers';

import { RegisterData } from '@shared/models/Auth';
import { AccountService } from '@shared/services/account.service';
import { AuthService } from '@shared/services/auth.service';
import { LanguageListItem, LanguageService } from '@shared/services/language.service';
import { TrackingService } from '@shared/services/tracking.service';
import { getCookie, removeCookie, setCookie } from '@shared/utils';

import { environment } from '../../environments/environment';
import { REGISTER_PAGES } from '../app.constants';

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready(cb: () => void): void;
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

@Component({
  selector: 'app-register-form',
  templateUrl: './register-form.component.html',
  styleUrls: ['./register-form.component.scss'],
  standalone: false
})
export class RegisterFormComponent implements OnInit, OnDestroy {
  public static SESSION_STORAGE_KEY = 'accessToken';

  protected readonly onDestroy$ = new Subject<void>();
  @ViewChild('registerFormEl') registerFormEl: FormGroupDirective;

  registerForm: FormGroup;
  user = undefined;
  error_message = false;
  exists_message = false;
  loading = false;
  incorrectPhone = false;
  isPasswordInvalid = true;
  response: {
    status: string;
    data: Customer;
  };

  hidePasswordHints = true;
  showPass = false;
  mobile: boolean;
  country: string;
  countryCode: string;
  selectedLanguage: LanguageListItem;
  pti: string;
  private recaptchaLanguage: string;

  captchaScriptPromise?: Promise<void>;

  get emailControl(): AbstractControl | null {
    return this.registerForm.get('email');
  }

  get passwordControl(): AbstractControl | null {
    return this.registerForm.get('password');
  }

  get recaptchaTokenControl(): AbstractControl | null {
    return this.registerForm.get('recaptcha_token');
  }

  get languageControl(): AbstractControl | null {
    return this.registerForm.get('language');
  }

  get providerControl(): AbstractControl | null {
    return this.registerForm.get('provider');
  }

  get tokenControl(): AbstractControl | null {
    return this.registerForm.get('token');
  }

  get phoneControl(): AbstractControl | null {
    return this.registerForm.get('phone');
  }

  get optinControl(): AbstractControl | null {
    return this.registerForm.get('optin');
  }

  get locale() {
    return this.languageService.getRedirectLanguage();
  }

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    public http: HttpClient,
    private router: Router,
    private languageService: LanguageService,
    private trackingService: TrackingService,
    private accountService: AccountService,
    private store: Store,
    private route: ActivatedRoute,
    private amplitudeService: AmplitudeService
  ) {
    this.route.params.subscribe((params) => {
      if (params['code']) {
        this.store.dispatch(AppRootActions.fetchReferralRewards({ referralCode: params['code'] }));
      }
    });

    this.store.pipe(select(fromAppRoot.selectReferralRewardStatus), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (value === 'fail') {
        const lang = this.languageService.selected.value.split('-')[0];
        void this.router.navigate([REGISTER_PAGES[lang]]);
      }
    });
  }

  ngOnInit(): void {
    if (document.referrer.length > 0) {
      document.cookie = `referrer=${document.referrer}`;
    }

    this.setVisitor();
    this.setGCLID();
    this.setPTI();

    this.trackingService.setSTIFromURL(true);

    this.checkMobile();

    this.registerForm = this.getForm();
    this.recaptchaLanguage = this.getRecaptchaLanguage();

    this.languageService.selected.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      const nextLanguage = this.getRecaptchaLanguage();
      if (nextLanguage !== this.recaptchaLanguage) {
        this.recaptchaLanguage = nextLanguage;
        this.cleanupEnterpriseCaptcha();
      }
    });
  }

  newsletterOptin() {
    this.optinControl.setValue(!this.optinControl?.value);
  }

  getRedirectUrlForPrivacyPolicy(): string {
    const { host } = window.location;
    const isSwiss = host.includes('.ch');
    const domain = isSwiss ? 'ch' : 'de';

    if (this.languageService.getRedirectLanguage() === 'de') {
      return `https://inyova.${domain}/${isSwiss ? 'datenschutzhinweise' : 'datenschutz'}/`;
    }
    if (this.languageService.getRedirectLanguage() === 'fr') {
      return `https://inyova.${domain}/fr/confidentialite-des-donnees/`;
    }
    return `https://inyova.${domain}/en/data-protection/`;
  }

  setVisitor(): void {
    const hasVisited = document.cookie.match(RegExp('(?:^|;\\s*)has_visited=([^;]*)'));
    if (hasVisited === null) {
      document.cookie = 'has_visited=1';
      this.trackVisitor();
    }
  }

  trackVisitor(): void {
    const yovaSource = document.cookie.match(RegExp('(?:^|;\\s*)_yova_source=([^;]*)'));
    const sti = yovaSource !== null ? getCookie('_yova_source') : '';

    const lang = this.languageService.selected.getValue();

    this.http
      .post(`${environment.apiUrl}/prospects/visitor`, {
        _yova_source: sti,
        language: lang
        // , visitor_version: ''
      })
      .subscribe();
  }

  setGCLID(): void {
    let gclid: string;
    let wbraid: string;
    let gbraid: string;

    this.route.queryParamMap.subscribe((queryParams) => {
      // get GCLID from the query
      gclid = queryParams.get('gclid');
      wbraid = queryParams.get('wbraid');
      gbraid = queryParams.get('gbraid');
    });

    if (gclid !== null) {
      setCookie('gclid', gclid, 7);
    }
    if (wbraid !== null) {
      setCookie('wbraid', wbraid, 7);
    }
    if (gbraid !== null) {
      setCookie('gbraid', gbraid, 7);
    }
  }

  setPTI(): void {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.pti = queryParams.get('pti');
    });
  }

  getForm(): FormGroup {
    return this.formBuilder.group({
      login: [''],
      email: ['', [Validators.required, Validators.email]],
      recaptcha_token: ['', Validators.required],
      phone: [''],
      _yova_source: '',
      _yova_product_type: '',
      referral: '',
      referrer: '',
      provider: 'email',
      uid: '',
      password: [''],
      password_confirmation: [''],
      language: this.languageService.selected.getValue(),
      optin: false,
      token: '',
      gclid: '',
      wbraid: '',
      gbraid: ''
    });
  }

  async googleSignup(token: string): Promise<void> {
    this.registerForm.markAsUntouched();
    this.registerFormEl.resetForm(this.registerForm.value);

    this.providerControl.setValue('google');
    this.tokenControl.setValue(token);
    this.languageControl.setValue(this.languageService.selected.getValue());

    try {
      await this.loadEnterpriseCaptcha();
      const recaptchaToken = await this.executeEnterpriseCaptcha();
      this.recaptchaTokenControl?.setValue(recaptchaToken);
    } catch {
      this.recaptchaTokenControl?.reset('');
      return;
    }

    sessionStorage.setItem(RegisterFormComponent.SESSION_STORAGE_KEY, token);

    this.loading = true;

    this.onSubmit();
  }

  async sendForm(): Promise<void> {
    if (this.isPasswordInvalid || this.hasInvalidRegistrationFields()) {
      return;
    }

    try {
      this.providerControl.setValue('email');
      this.updateFormCookies();

      await this.loadEnterpriseCaptcha();
      const token = await this.executeEnterpriseCaptcha();
      this.recaptchaTokenControl?.setValue(token);

      this.loading = true;
      this.onSubmit();
    } catch {
      this.loading = false;
      this.error_message = true;
      this.exists_message = false;
      this.incorrectPhone = false;
      this.recaptchaTokenControl?.reset('');
    }
  }

  private hasInvalidRegistrationFields(): boolean {
    const controlsToCheck = ['email', 'password', 'phone'];
    return controlsToCheck.some((controlName) => this.registerForm.get(controlName)?.invalid);
  }

  updateFormCookies() {
    this.registerForm.get('_yova_source').setValue(getCookie('_yova_source'));
    this.registerForm.get('_yova_product_type').setValue(getCookie('_yova_product_type'));
    this.registerForm.get('referral').setValue(getCookie('referral'));
    this.registerForm.get('referrer').setValue(getCookie('referrer'));
    this.registerForm.get('gclid').setValue(getCookie('gclid'));
    this.registerForm.get('wbraid').setValue(getCookie('wbraid'));
    this.registerForm.get('gbraid').setValue(getCookie('gbraid'));
  }

  // Submit form and send it to backend
  onSubmit(): void {
    window.focus(); // focus on tab
    parent.focus(); // focus on tab

    // if the provider google don't check form fields.... tweak for unset surname on google
    if (this.registerForm.value.provider !== 'google') {
      if (this.registerForm.invalid) {
        return;
      }
    }

    // Adding missing values for tokenService
    this.registerForm.get('login').setValue(this.registerForm.value.email);
    window.localStorage.clear();

    // update language
    this.languageControl.setValue(this.languageService.selected.getValue());

    this.authService.registerAccount(this.registerForm.value as RegisterData).subscribe(
      ({ body }) => {
        this.response = body;
        const customer = body.data;
        this.amplitudeService.setUserProperties(customer);
        this.amplitudeService.trackEvent('Signup Submitted', {
          signup_type: customer.provider,
          displayed_language: customer.language,
          displayed_country: customer.app_location
        });
      },
      (error) => {
        this.loading = false;
        if (error.status === 422) {
          if (error.error.code === 'invalid') {
            this.exists_message = true;
            this.error_message = false;
            this.incorrectPhone = false;
            return;
          }
          if (error.error.code === 'invalid_token') {
            this.exists_message = false;
            this.error_message = false;
            this.incorrectPhone = false;
            this.recaptchaTokenControl?.reset('');
            return;
          }
          if (error.error.code === 'invalid_phone') {
            this.exists_message = false;
            this.error_message = false;
            this.incorrectPhone = true;
            return;
          }
        }
        this.error_message = true;
        this.exists_message = false;
        this.incorrectPhone = false;
      },
      () => {
        if (this.response.status === 'success') {
          this.exists_message = false;
          this.error_message = false;
          this.incorrectPhone = false;

          this.authService.updateLoggedIn(true);
          this.trackingService.trackLastTouchPoint();

          removeCookie('gclid');

          // handle DE/AT direct 3B account needs
          // handle Inyova Pro with direct 3B as well
          if (this.response.data.inyova_pro || this.response.data.app_location !== 'ch') {
            this.createDefault3BProduct(this.response.data);
            return;
          }

          // handle multiple product needs
          if (this.pti === '3a') {
            this.store.dispatch(AppRootActions.createNewAccount({ kind: '3a', appLocation: 'ch' }));
          } else {
            this.store.dispatch(AppRootActions.fetchCustomerSuccess({ customer: this.response.data, selectActiveAccount: false }));
            this.loading = false;
            void this.router.navigate(['/']);
          }
        } else {
          this.loading = false;
          this.error_message = true;
          this.exists_message = false;
          this.incorrectPhone = false;
        }
      }
    );
  }

  private createDefault3BProduct(customer): void {
    this.accountService.createAccount({ owner_name: '', kind: '3b' }).subscribe(
      (apiAccount) => {
        localStorage.setItem('currentAccountID', apiAccount.id);
        this.store.dispatch(
          AppRootActions.fetchCustomerSuccess({
            customer: {
              ...customer,
              accounts: [
                {
                  id: apiAccount.id,
                  owner_name: apiAccount.owner_name,
                  kind: apiAccount.kind,
                  step_status: apiAccount.step_status
                }
              ],
              temporary_accounts: [
                {
                  id: apiAccount.id,
                  owner_name: apiAccount.owner_name,
                  kind: apiAccount.kind,
                  step_status: apiAccount.step_status
                }
              ]
            },
            selectActiveAccount: false
          })
        );
        this.store.dispatch(AppRootActions.fetchCustomerAccountDetailSuccess({ customerAccountDetail: transformAccount(apiAccount) }));
        this.loading = false;
        void this.router.navigate(['/']);
      },
      () => {
        this.loading = false;
        this.error_message = true;
        this.exists_message = false;
        this.incorrectPhone = false;
      }
    );
  }

  passVisibility(): void {
    this.showPass = !this.showPass;
    document.getElementById('track_pass_input').focus();
  }

  checkMobile(): void {
    const { innerWidth } = window;
    if (innerWidth <= 768) {
      this.mobile = true;
    } else {
      this.mobile = false;
    }
  }

  onShowLogin() {
    void this.router.navigate(['/login']);
  }

  onIsPasswordInvalid(event) {
    setTimeout(() => {
      this.isPasswordInvalid = event;
    });
  }

  loadEnterpriseCaptcha(): Promise<void> {
    if (window.grecaptcha?.enterprise) {
      return new Promise<void>((resolve) => window.grecaptcha!.enterprise.ready(() => resolve()));
    }

    if (this.captchaScriptPromise) {
      return this.captchaScriptPromise;
    }

    this.captchaScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha-enterprise="true"]');

      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA Enterprise')), {
          once: true
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.reCaptchaSiteKey}&hl=${this.getRecaptchaLanguage()}`;
      script.async = true;
      script.defer = true;
      script.dataset.recaptchaEnterprise = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA Enterprise'));
      document.head.appendChild(script);
    });
    return this.captchaScriptPromise;
  }

  private getRecaptchaLanguage(): string {
    const lang = this.languageService.getRedirectLanguage();
    return ['de', 'en', 'fr'].includes(lang) ? lang : 'en';
  }

  private executeEnterpriseCaptcha(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const grecaptcha = window.grecaptcha?.enterprise;

      if (!grecaptcha) {
        reject(new Error('reCAPTCHA Enterprise is not available'));
        return;
      }

      grecaptcha.ready(() => {
        grecaptcha.execute(environment.reCaptchaSiteKey, { action: 'registration' }).then(resolve).catch(reject);
      });
    });
  }

  cleanupEnterpriseCaptcha(): void {
    this.captchaScriptPromise = undefined;
    document.querySelector('script[data-recaptcha-enterprise="true"]')?.remove();
    document.querySelector('.grecaptcha-badge')?.remove();
    delete (window as Window & { grecaptcha?: unknown }).grecaptcha;
  }

  @HostListener('window:resize') onResize(): void {
    this.checkMobile();
  }

  ngOnDestroy(): void {
    this.cleanupEnterpriseCaptcha();

    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
